import * as React from "react";
import { Box, Card, useMediaQuery } from "@material-ui/core";
import { CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import { PostHog } from "posthog-js";
import { usePostHog } from "posthog-js/react";
import { Link, Redirect, withRouter } from "react-router-dom";

import { FormInput, FormRow, Image } from "components";
import { PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from "constants/index";
import { Desktop, Div, Mobile } from "containers/Responsive";
import { AbstractRootComponent } from "containers/Responsive/AbstractRootComponent";
import { AppContext } from "contexts";
import {
  getQueryParams,
  keepNumbers,
  listInvalidCharacters,
  mountQueryString,
  stringIsValid,
  validateCountryCode,
  validateEmail,
  validatePassword,
} from "lib/helpers";
import * as routes from "routes";
import {
  completeSellerAccount,
  createNewSeller,
  getCurrentUser,
  getEmailExists,
} from "services/users";
import { User } from "services/users/types";
import theme from "theme";

import { countriesOptions } from "./countries";
import { formatPhoneNumber } from "./formatter";
import SocialLogin from "./SocialLogin";

// Container component for form sections
const Container: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box width="100%" padding={theme.spacing(0, 5)}>
      {children}
    </Box>
  );
};

const MIN_FIRST_NAME_LENGTH = 2;
const MIN_LAST_NAME_LENGTH = 2;

interface SellSideHeaderProps {
  message?: string;
  mobileMessage?: string;
  onClick?: () => void;
  logo: string;
  children?: React.ReactNode;
  extraStyle?: React.CSSProperties;
}

type ConnectAssetScreen = "CREDENTIALS" | "SELECT_ACCOUNT" | "SUCCESS";

type CurrentScreen = "DEFAULT" | ConnectAssetScreen;

export const SellSideHeader = (props: SellSideHeaderProps) => {
  const inner = (
    <>
      <Desktop>
        <span id="back-to-account-desktop">{props.message}</span>
      </Desktop>
      <Mobile>
        <span id="back-to-account-mobile">
          {props.mobileMessage || props.message}
        </span>
      </Mobile>
    </>
  );

  return (
    <>
      <div className="account-creation-header-wrapper">
        <div className="account-creation-header" style={props.extraStyle}>
          {props.message && props.onClick && (
            <span className="deal-page-back-button">
              <span
                style={{ marginTop: "auto", marginBottom: "auto" }}
                onClick={() => props.onClick()}
              >
                <i
                  className="fa fa-angle-left"
                  style={{ fontSize: "22px", marginRight: "10px" }}
                  aria-hidden="true"
                />
                {inner}
              </span>
            </span>
          )}
          <Image
            alt={props.mobileMessage || props.message}
            name={props.logo}
            style={{
              maxWidth: "50%",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          />
        </div>
      </div>
      {props.children}
    </>
  );
};

export interface FormRootProps {
  children?: React.ReactNode;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  className?: string;
  formId?: string;
}
export const FormRoot = (props: FormRootProps) => {
  const { children, onSubmit, className, formId } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <>
      {isMobile ? (
        <form
          id={`${formId || "form-seller-account"}`}
          className={`mobile ${className}`}
          onSubmit={onSubmit}
        >
          {children}
        </form>
      ) : (
        <form
          id={`${formId || "form-seller-account"}`}
          className={`desktop ${className}`}
          onSubmit={onSubmit}
        >
          {children}
        </form>
      )}
    </>
  );
};

export interface NewAccount {
  firstName: string;
  lastName: string;
  country: CountryCode;
  phone: string;
  email: string;
  password: string;
}

export interface Email {
  value: string;
  exists: boolean;
}

export interface NewAccountFormProps {
  setUserInfo: (userInfo: User) => void;
  socialSignUp?: boolean;
  userInfo?: User;
  formTitle: string;
  posthog: PostHog | null;
}

export interface NewAccountFormState {
  account: NewAccount;
  emailExists: Email;
  passwordConfirm: string;
  showFormWarning: boolean;
  showUserUnauthenticated: boolean;
  showUnexpectedError: boolean;
  showSocialLogin: boolean;
}

export class NewAccountForm extends React.Component<
  NewAccountFormProps,
  NewAccountFormState
> {
  constructor(props: NewAccountFormProps) {
    super(props);
    const { socialSignUp } = props;
    this.state = {
      account: {
        country: socialSignUp ? props.userInfo.country || "US" : "US",
        email: "",
        firstName: socialSignUp ? props.userInfo.firstName : "",
        lastName: socialSignUp ? props.userInfo.lastName : "",
        password: "",
        phone: socialSignUp ? props.userInfo.phone.toString() : "",
      },
      emailExists: { value: "", exists: false },
      passwordConfirm: "",
      showFormWarning: false,
      showUserUnauthenticated: false,
      showUnexpectedError: false,
      showSocialLogin: !socialSignUp,
    };

    this.onSubmit = this.onSubmit.bind(this);
  }

  private getLoginURL() {
    const next = getQueryParams().next;

    if (next) {
      return routes.sellerLogin() + mountQueryString({ next });
    }

    return routes.sellerLogin();
  }

  private updateFormState(
    key: keyof NewAccount | "passwordConfirm",
    value: string,
    maxLength?: number,
  ) {
    this.setState(prevState => {
      let newValue = value;
      if (maxLength) {
        newValue = value.slice(0, maxLength);
      }
      if (key === "passwordConfirm") {
        return {
          ...prevState,
          passwordConfirm: newValue,
          showFormWarning: false,
        };
      }
      return {
        ...prevState,
        account: {
          ...prevState.account,
          [key]: newValue,
        },
        showCountryCode: true,
        showFormWarning: false,
      };
    });
  }

  private async verifyIfEmailExists(value: string) {
    if (this.state.emailExists.value === value) {
      return;
    }
    const response = await getEmailExists(value);
    this.setState(prevState => {
      return {
        ...prevState,
        emailExists: {
          exists: response.status === 200,
          value,
        },
      };
    });
  }

  private validate(
    key: keyof NewAccount | "passwordConfirm",
    value: string,
  ): boolean {
    if (!stringIsValid(value)) {
      return false;
    }
    const { socialSignUp } = this.props;
    switch (key) {
      case "firstName": {
        return value.length >= MIN_FIRST_NAME_LENGTH;
      }
      case "lastName": {
        return value.length >= MIN_LAST_NAME_LENGTH;
      }
      case "country": {
        return validateCountryCode(value);
      }
      case "phone": {
        return isValidPhoneNumber(value, this.state.account.country);
      }
      case "email": {
        return socialSignUp || validateEmail(value);
      }
      case "password": {
        return socialSignUp || validatePassword(value);
      }
      case "passwordConfirm": {
        return socialSignUp || value === this.state.account.password;
      }
    }
  }

  private validateAll(): boolean {
    const keys = Object.keys(this.state.account);
    const validation = keys
      .map(key =>
        this.validate(
          key as keyof NewAccount,
          this.state.account[key as keyof NewAccount],
        ),
      )
      .reduce((acc: boolean, value: boolean) => acc && value, true);

    const { password } = this.state.account;
    const { passwordConfirm } = this.state;
    const passwordMatch = password === passwordConfirm;
    return validation && passwordMatch;
  }

  private async createAccount() {
    const { setUserInfo, posthog } = this.props;
    const { account } = this.state;
    const user = await createNewSeller(account, posthog);
    setUserInfo(user);
  }

  private async completeAccount() {
    const data = (({ country, firstName, lastName, phone }) => ({
      country,
      firstName,
      lastName,
      phone,
    }))(this.state.account);
    try {
      const userInfo = await completeSellerAccount(data);
      this.props.setUserInfo(userInfo);
    } catch (e) {
      try {
        const user = await getCurrentUser();
        if (!user.userId) {
          this.setState({ showUserUnauthenticated: true });
        } else {
          this.setState({ showUnexpectedError: true });
        }
      } catch {
        this.setState({ showUnexpectedError: true });
      }
    }
  }

  private onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!this.validateAll()) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      this.setState({
        showFormWarning: true,
      });
      return;
    }
    if (this.props.socialSignUp) {
      this.completeAccount();
    } else {
      this.createAccount();
    }
  }

  public render() {
    const {
      firstName,
      lastName,
      phone: phoneNumber,
      email: emailAddress,
      country,
      password,
    } = this.state.account;

    const {
      emailExists,
      passwordConfirm,
      showFormWarning,
      showUserUnauthenticated,
      showUnexpectedError,
      showSocialLogin,
    } = this.state;

    const { formTitle, socialSignUp } = this.props;

    const showEmailExistsMessage =
      emailExists.exists && emailExists.value === emailAddress;

    const invalidCharacters = listInvalidCharacters(
      Object.values(this.state.account).join(""),
    );
    const tooShortFirstName = firstName.length < MIN_FIRST_NAME_LENGTH;
    const tooShortLastName = lastName.length < MIN_LAST_NAME_LENGTH;

    return (
      <Card>
        <div className="contact-info-and-login">
          <h2
            style={{
              marginBottom: "0.9375rem",
            }}
          >
            {formTitle}
          </h2>
          <Container>
            <p style={{ fontSize: "inherit", color: "#2A313B" }}>
              The first step in raising money is creating a secure connection to
              Royalty Exchange.
            </p>
            <p style={{ fontSize: "inherit", color: "#2A313B" }}>
              Enter your contact information below to establish a secure
              account.
            </p>
          </Container>
          {showSocialLogin && (
            <SocialLogin
              onGoToLogin={this.getLoginURL}
              onRegularSignUp={() => this.setState({ showSocialLogin: false })}
            />
          )}
          {!showSocialLogin && showEmailExistsMessage && (
            <Container>
              <div className="create-account-warning">
                <p style={{ fontSize: "inherit" }}>
                  There is already an account associated with this email
                  address.
                </p>
                <p style={{ fontSize: "inherit" }}>
                  <a style={{ fontSize: "inherit" }} href="/seller/login/">
                    <u>Sign in using that email</u>
                  </a>{" "}
                  or, create an account using a different email.
                </p>
              </div>
            </Container>
          )}
          {!showSocialLogin && showFormWarning && (
            <Container>
              <div className="create-account-warning">
                <p style={{ fontSize: "inherit" }}>
                  Some fields in your form are invalid, please check.
                </p>
                {invalidCharacters && invalidCharacters.length > 0 && (
                  <p style={{ fontSize: "inherit" }}>
                    The following characters are not allowed:{" "}
                    {invalidCharacters.join(", ")}
                  </p>
                )}
                {tooShortFirstName && (
                  <p style={{ fontSize: "inherit" }}>
                    First Name field should have at least{" "}
                    {MIN_FIRST_NAME_LENGTH} characters.
                  </p>
                )}
                {tooShortLastName && (
                  <p style={{ fontSize: "inherit" }}>
                    Last Name field should have at least {MIN_LAST_NAME_LENGTH}{" "}
                    characters.
                  </p>
                )}
              </div>
            </Container>
          )}
          {!showSocialLogin && showUserUnauthenticated && (
            <Container>
              <div className="create-account-warning">
                <p>You are not logged, please login!</p>
              </div>
            </Container>
          )}
          {!showSocialLogin && showUnexpectedError && (
            <Container>
              <div className="create-account-warning">
                <p>Unexpected error, please try again in a few minutes!</p>
              </div>
            </Container>
          )}
          {!showSocialLogin && (
            <FormRoot onSubmit={this.onSubmit}>
              <FormRow className="seller-create-account-wrapable-row">
                <FormInput
                  id="first-name"
                  title="First Name*"
                  placeholder="John"
                  onChange={value => this.updateFormState("firstName", value)}
                  required
                  flexBasis="48%"
                  value={firstName}
                  validator={(value: string) =>
                    this.validate("firstName", value)
                  }
                  autoComplete="given-name"
                />
                <FormInput
                  id="last-name"
                  title="Last Name*"
                  placeholder="Smith"
                  onChange={value => this.updateFormState("lastName", value)}
                  required
                  flexBasis="48%"
                  value={lastName}
                  validator={(value: string) =>
                    this.validate("lastName", value)
                  }
                  autoComplete="family-name"
                />
              </FormRow>
              <FormRow className="seller-create-account-wrapable-row">
                <FormInput
                  id="country"
                  title="Country*"
                  type="select"
                  options={countriesOptions}
                  onChange={value =>
                    this.updateFormState("country", value.toUpperCase())
                  }
                  required
                  value={country}
                  flexBasis="48%"
                  validator={(value: string) => this.validate("country", value)}
                  hideValidationIcon
                  autoComplete="tel-country-code"
                  className="country-form-field"
                />
                <FormInput
                  id="phone"
                  title="Phone Number*"
                  type="tel"
                  required
                  onChange={(value: string) =>
                    this.updateFormState("phone", keepNumbers(value), 12)
                  }
                  value={formatPhoneNumber(phoneNumber)}
                  flexBasis="48%"
                  validator={(value: string) => this.validate("phone", value)}
                  autoComplete="tel-national"
                />
              </FormRow>
              {!socialSignUp && (
                <>
                  <FormRow>
                    <FormInput
                      id="email"
                      title="Email Address*"
                      type="email"
                      onChange={value => this.updateFormState("email", value)}
                      required
                      placeholder="johnsmith@email.com"
                      value={emailAddress}
                      validator={(value: string) =>
                        this.validate("email", value)
                      }
                      autoComplete="email"
                      callbackIfValid={(value: string) =>
                        this.verifyIfEmailExists(value)
                      }
                    />
                  </FormRow>
                  <FormRow
                    bottom={
                      <div
                        className="password-requisite-hint"
                        style={{ textAlign: "left", fontSize: "12px" }}
                      >
                        <p style={{ fontSize: "inherit" }}>
                          Must contain: 8+ characters, upper and lower case
                          letters, numbers and/or symbols.
                        </p>
                      </div>
                    }
                  >
                    <FormInput
                      id="id-password1"
                      title="Create Password*"
                      type="password"
                      onChange={value =>
                        this.updateFormState("password", value)
                      }
                      required
                      value={password}
                      placeholder="••••••••"
                      validator={value => this.validate("password", value)}
                      maxLength={120}
                      autoComplete="new-password"
                    />
                  </FormRow>
                  <FormRow>
                    <FormInput
                      id="id-password2"
                      title="Confirm Password*"
                      type="password"
                      onChange={value =>
                        this.updateFormState("passwordConfirm", value)
                      }
                      required
                      value={passwordConfirm}
                      placeholder="••••••••"
                      validator={value =>
                        this.validate("passwordConfirm", value)
                      }
                      maxLength={120}
                      autoComplete="new-password"
                    />
                  </FormRow>
                </>
              )}
              <input
                id="seller-account-create-next-step"
                data-attr="create-royx-account-button"
                data-ph-capture-attribute-is-seller="true"
                className="button -solid -green"
                style={{
                  margin: 0,
                  padding: "20px 10px",
                  marginTop: "10px",
                  marginBottom: "20px",
                  border: "none",
                }}
                type="submit"
                value="Verify Contact Information"
              />
              {!socialSignUp && (
                <div style={{ textAlign: "center" }}>
                  or{" "}
                  <Link
                    to={this.getLoginURL()}
                    style={{ textDecoration: "underline" }}
                  >
                    Log in to an existing account
                  </Link>
                </div>
              )}
              <p
                style={{
                  textAlign: "center",
                  fontSize: "80%",
                  marginTop: "20px",
                  width: "80%",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                By clicking above, you acknowledge that you have read and agree
                to our&nbsp;
                <a
                  href={TERMS_AND_CONDITIONS_URL}
                  className="link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </a>
                &nbsp;and&nbsp;
                <a
                  href={PRIVACY_POLICY_URL}
                  className="link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy.
                </a>
              </p>
            </FormRoot>
          )}
        </div>
      </Card>
    );
  }
}

NewAccountForm.contextType = AppContext;

const NewAccountFormWithPostHog: React.FC<
  Omit<NewAccountFormProps, "posthog">
> = props => {
  const posthog = usePostHog();
  return <NewAccountForm {...props} posthog={posthog} />;
};

type AccountCreationModalState =
  | "create-account"
  | "create-account-referral"
  | "complete-account"
  | "login";

export interface SellSideAccountBaseProps {
  page: AccountCreationModalState;
}

export interface SellSideAccountBaseState {
  userInfo?: User;
  screen: CurrentScreen;
  prevScreen?: CurrentScreen;
}

export class SellSideAccountBase extends React.Component<
  SellSideAccountBaseProps,
  SellSideAccountBaseState
> {
  static contextType = AppContext;
  context!: React.ContextType<typeof AppContext>;

  next?: string;

  constructor(props: SellSideAccountBaseProps) {
    super(props);

    this.state = { screen: "DEFAULT" };

    const next = getQueryParams().next;
    this.next = next ? decodeURI(next) : null;
  }

  private async getUserInfo() {
    const userInfo = await getCurrentUser();
    this.setState({ userInfo });
    this.context.setUser(userInfo);
  }

  public componentDidMount() {
    this.getUserInfo();
  }

  private redirect(): JSX.Element {
    return <Redirect to={this.next || routes.sellerDashboard()} />;
  }

  private redirectToCompleteAccount(): JSX.Element {
    return <Redirect to={routes.sellerCompleteAccount()} />;
  }

  private redirectToCreateAccount(): JSX.Element {
    return <Redirect to={routes.sellerCreateAccount()} />;
  }

  public render() {
    const { page } = this.props;
    const { userInfo, prevScreen, screen } = this.state;
    const noBack = ["CREDENTIALS", "DEFAULT", "SUCCESS"];

    // Do not render the page while userInfo endpoint hasn't returned
    if (userInfo === undefined || userInfo === null) {
      return null;
    }

    let component: JSX.Element;
    let extraClass = "";

    switch (page) {
      case "create-account-referral":
      case "create-account": {
        if (userInfo.userId && userInfo.isSetupFinalized) {
          return this.redirect();
        } else if (userInfo.userId) {
          return this.redirectToCompleteAccount();
        }
        component = (
          <NewAccountFormWithPostHog
            setUserInfo={userInfo => {
              this.context.setUser(userInfo);
              return this.setState({ userInfo });
            }}
            formTitle={
              page === "create-account-referral"
                ? "Claim Your Free Valuation"
                : "Create a Secure Connection"
            }
          />
        );
        extraClass = "sell-side-create-account-root";
        break;
      }
      case "complete-account": {
        if (!userInfo.userId) {
          return this.redirectToCreateAccount();
        } else if (userInfo.isSetupFinalized) {
          return this.redirect();
        }
        component = (
          <NewAccountFormWithPostHog
            setUserInfo={userInfo => {
              this.context.setUser(userInfo);
              return this.setState({ userInfo });
            }}
            userInfo={userInfo}
            formTitle="Create a Secure Connection"
            socialSignUp
          />
        );
        extraClass = "sell-side-complete-account-root";
        break;
      }
    }

    return (
      <Div className="account-creation-root-container">
        <div className={`account-creation-root ${extraClass}`}>
          <SellSideHeader
            message={noBack.indexOf(screen) === -1 ? "Back" : undefined}
            onClick={() => this.updateScreen(prevScreen)}
            logo="orderbook/royalty_exchange_logo.svg"
          />
          {component}
        </div>
      </Div>
    );
  }

  private updateScreen(update: CurrentScreen) {
    const { screen } = this.state;

    this.setState({
      prevScreen: screen,
      screen: update,
    });
  }
}

class CreateAccountEntryBase extends AbstractRootComponent {
  public render() {
    return <SellSideAccountBase page="create-account" />;
  }
}

class CreateAccountReferralEntryBase extends AbstractRootComponent {
  public render() {
    return <SellSideAccountBase page="create-account-referral" />;
  }
}

class CompleteAccountEntryBase extends AbstractRootComponent {
  public render() {
    return <SellSideAccountBase page="complete-account" />;
  }
}

export const CreateAccountEntry = withRouter(CreateAccountEntryBase);
export const CreateAccountReferralEntry = withRouter(
  CreateAccountReferralEntryBase,
);
export const CompleteAccountEntry = withRouter(CompleteAccountEntryBase);
